import React from 'react';
import Helmet from 'react-helmet';
import { Route } from 'react-router-dom';
import Waypoint from 'react-waypoint';

import TaskMaterials from 'api/resources/taskMaterials';
import getErrors from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';

import MaterialPage from 'pages/MaterialPage';
import ChatsContainer from 'chats/ChatsContainer';

import MaterialListItem from 'admin-moderator/components/MaterialListItem';
import MaterialListItemStub from 'admin-moderator/components/MaterialListItemStub';

import Tab from 'components/Tab';
import MaterialNotFound from 'components/MaterialNotFound';

import RightColumn from 'styled/Page/RightColumn';
import ErrorText from 'styled/Page/ErrorText';
import Main from 'styled/Page/Main';
import CenteredContainer from 'styled/Page/CenteredContainer';
import FilterWrapper from 'styled/Page/FilterWrapper';
import List from 'styled/List';

const TABS = [{
  label: 'Новые',
  filter: 'created',
}, {
  label: 'Одобренные',
  filter: 'accepted',
}, {
  label: 'Отклоненные',
  filter: 'rejected',
}];

const PAGE_SIZE = 49;

class MaterialsPage extends React.PureComponent {
  chats = React.createRef();

  state = {
    activeTab: TABS[0],

    materials: [],
    loading: false,
    errors: [],
    hasMore: false,
  }

  componentDidMount() {
    this.loadMaterials();
  }

  get cursor() {
    if (this.state.materials.length === 0) return undefined;
    return this.state.materials[this.state.materials.length - 1].id;
  }

  get sort() {
    if (this.state.activeTab.filter === 'accepted') return '-approved_at';
    if (this.state.activeTab.filter === 'rejected') return '-rejected_at';
    return '-created_at';
  }

  handleTabChange = (activeTab) => {
    this.setState({
      activeTab,
      materials: [],
    }, this.loadMaterials);
  }

  loadMaterials = async () => {
    this.setState({
      loading: true,
      errors: [],
    });

    try {
      const response = await TaskMaterials.getAll({
        page: {
          cursor: this.cursor,
          size: PAGE_SIZE,
        },
        filter: { status: this.state.activeTab.filter },
        fields: {
          task_material: 'title,file',
          image: 'url',
          video: 'cover',
        },
        include: 'file,file.cover',
        sort: this.sort,
      });

      const materials = await new Deserializer({
        keyForAttribute: 'camelCase',
        video: {
          valueForRelationship: (relationship, included) => ({
            ...included,
            type: relationship.type,
          }),
        },
        image: {
          valueForRelationship: (relationship, included) => ({
            ...included,
            type: relationship.type,
          }),
        },
      }).deserialize(response);

      this.setState(prevState => ({
        materials: materials.reduce((acc, currentValue) => {
          if (acc.find(item => item.id === currentValue.id)) {
            return acc;
          }

          return acc.concat(currentValue);
        }, prevState.materials),
        loading: false,
        hasMore: materials.length === PAGE_SIZE,
      }));
    } catch (err) {
      this.setState({ loading: false });

      const errors = await getErrors(err);
      this.setState({ errors });
    }
  }

  handleMaterialStatusUpdate = (material) => {
    this.setState(prevState => ({
      materials: material.status !== prevState.activeTab.value
        ? prevState.materials.filter(item => item.id !== material.id)
        : [material].concat(prevState.materials),
    }));
  }

  handleSendToAuthorClick = (room) => {
    if (!this.chats || !this.chats.current) return;
    this.chats.current.loadAndOpenModeratorChatForUser(room);
  }

  render() {
    return (
      <Main>
        <Helmet><title>Материалы</title></Helmet>

        <div style={{ padding: '0 32px 0 24px', width: '100%' }}>
          <Route
            path="/materials/:materialId"
            render={() => (
              <MaterialPage
                onUpdate={this.handleMaterialStatusUpdate}
                onSendToAuthor={this.handleSendToAuthorClick}
              />
            )}
          />

          <div style={{ paddingBottom: '100px' }}>
            <FilterWrapper style={{ margin: '0' }}>
              {TABS.map(tab => (
                <Tab
                  key={tab.label}
                  tab={tab}
                  active={tab.label === this.state.activeTab.label}
                  onChange={this.handleTabChange}
                />
              ))}
            </FilterWrapper>

            <List>
              {this.state.materials.map(material => (
                <MaterialListItem
                  key={material.id}
                  id={material.id}
                  cover={material.file && (material.file.cover ? material.file.cover.url : material.file.url)}
                  title={material.title}
                  mediaType={material.file && material.file.type}
                />
              ))}

              {this.state.loading && (
                Array.from({ length: PAGE_SIZE }, () => <MaterialListItemStub key={Math.random()} />)
              )}
            </List>

            {this.state.hasMore && (
              <Waypoint onEnter={this.loadMaterials} key={this.cursor} />
            )}

            {this.state.materials.length === 0 &&
              !this.state.loading &&
              this.state.errors.length === 0 && (
              <CenteredContainer><MaterialNotFound /></CenteredContainer>
            )}

            {!this.state.loading && this.state.errors.length > 0 && (
              <CenteredContainer>
                {this.state.errors.map(error => (
                  <ErrorText role="alert" key={error}>{error}</ErrorText>
                ))}
              </CenteredContainer>
            )}
          </div>
        </div>

        <RightColumn>
          <ChatsContainer ref={this.chats} />
        </RightColumn>
      </Main>
    );
  }
}

export default MaterialsPage;
